import { useQuery } from "@apollo/client";
import { IonPage, IonSplitPane } from "@ionic/react";
import { useContext, useEffect, useState } from "react";
import { Redirect, Route, Switch } from "react-router";

import ExpertAccountPage from "@components/expert/ExpertAccountPage";
import ExpertActivitiesPage from "@components/expert/ExpertActivitiesPage";
import ExpertAllJourneysPage from "@components/expert/ExpertAllJourneysPage";
import ExpertAutomationApplicationPage from "@components/expert/ExpertAutomationApplicationPage";
import ExpertAutomationApplicationsPage from "@components/expert/ExpertAutomationApplicationsPage";
import ExpertAutomationEditPage from "@components/expert/ExpertAutomationEditPage";
import ExpertAutomationNewPage from "@components/expert/ExpertAutomationNewPage";
import ExpertAutomationPage from "@components/expert/ExpertAutomationPage";
import ExpertAutomationsPage from "@components/expert/ExpertAutomationsPage";
import ExpertCarePlansPage from "@components/expert/ExpertCarePlansPage";
import ExpertCommunityPage from "@components/expert/ExpertCommunityPage";
import ExpertConsultConflictsPage from "@components/expert/ExpertConsultConflictsPage";
import ExpertConsultsPage from "@components/expert/ExpertConsultsPage";
import ExpertDashboardConsultsPage from "@components/expert/ExpertDashboardConsultsPage";
import ExpertDashboardCPTurnAroundTimePage from "@components/expert/ExpertDashboardCPTurnAroundTimePage";
import ExpertDashboardPage from "@components/expert/ExpertDashboardPage";
import ExpertDashboardSignUpsPage from "@components/expert/ExpertDashboardSignUpsPage";
import ExpertDashboardTimeLogsPage from "@components/expert/ExpertDashboardTimeLogsPage";
import ExpertEmployerPage from "@components/expert/ExpertEmployerPage";
import ExpertEmployersPage from "@components/expert/ExpertEmployersPage";
import ExpertGoalTemplateCreatePage from "@components/expert/ExpertGoalTemplateCreatePage";
import ExpertGoalTemplateEditPage from "@components/expert/ExpertGoalTemplateEditPage";
import ExpertGoalTemplatePage from "@components/expert/ExpertGoalTemplatePage";
import ExpertGoalTemplatesPage from "@components/expert/ExpertGoalTemplatesPage";
import ExpertJourneysPage from "@components/expert/ExpertJourneysPage";
import ExpertJourneyTagsPage from "@components/expert/ExpertJourneyTagsPage";
import ExpertManageExpertEditPage from "@components/expert/ExpertManageExpertEditPage";
import ExpertManageExpertJourneysPage from "@components/expert/ExpertManageExpertJourneysPage";
import ExpertManageExpertsPage from "@components/expert/ExpertManageExpertsPage";
import ExpertManageJourneyTaggingsPage from "@components/expert/ExpertManageJourneyTaggingsPage";
import ExpertManageJourneyTagsPage from "@components/expert/ExpertManageJourneyTagsPage";
import ExpertMenu from "@components/expert/ExpertMenu";
import ExpertMessagePage from "@components/expert/ExpertMessagePage";
import ExpertMessagesPage from "@components/expert/ExpertMessagesPage";
import ExpertMessageTemplateCategoriesPage from "@components/expert/ExpertMessageTemplateCategoriesPage";
import ExpertMessageTemplatesPage from "@components/expert/ExpertMessageTemplatesPage";
import ExpertOutOfOfficeSchedulePage from "@components/expert/ExpertOutOfOfficeSchedulePage";
import ExpertPackageGrantPage from "@components/expert/ExpertPackageGrantPage";
import ExpertResourceFeatureTemplateCreatePage from "@components/expert/ExpertResourceFeatureTemplateCreatePage";
import ExpertResourceFeatureTemplateEditPage from "@components/expert/ExpertResourceFeatureTemplateEditPage";
import ExpertResourceFeatureTemplatesPage from "@components/expert/ExpertResourceFeatureTemplatesPage";
import styles from "@components/expert/ExpertRouter.module.css";
import ExpertTasksPage from "@components/expert/ExpertTasksPage";
import ExpertTimeLogsPage from "@components/expert/ExpertTimeLogsPage";
import PageNotFoundWithMenuPage from "@components/PageNotFoundWithMenuPage";
import { sessionContext } from "@context/Contexts";
import useSubscribeToMessages from "@hooks/subscriptions/useSubscribeToMessages";
import useMyExpert from "@hooks/useMyExpert";
import { GetExpertDocument, GetExpertQuery, GetExpertQueryVariables } from "@typing/Generated";
import { useGoogleAuth } from "@utils/authUtils";

const ExpertRouter = () => {
  useSubscribeToMessages();
  const doGoogleAuth = useGoogleAuth();
  const [googleLoginStarted, setGoogleLoginStarted] = useState(false);

  const myExpert = useMyExpert();
  const { sessionState } = useContext(sessionContext);

  // It's possible for us to randomly lose access to the expert's calendar from Google. When this happens, the
  // expert record will have googleCalendarConnected = false, and we want to try to reconnect them to the calendar
  // as quickly as possible. This polls on getting the expert record and if it loses its Google connection it will
  // reload the page in order to force us to reauthenticate and reconnect.
  const getExpertQuery = useQuery<GetExpertQuery, GetExpertQueryVariables>(GetExpertDocument, {
    fetchPolicy: "network-only",
    pollInterval: 5 * 60 * 1000,
    skip: !myExpert,
    variables: { id: myExpert?.id ?? "" }
  });

  useEffect(() => {
    if (getExpertQuery.data?.expert.googleCalendarConnected === false) {
      window.location.reload();
    }
  }, [getExpertQuery.data?.expert.googleCalendarConnected]);

  useEffect(() => {
    if (myExpert && !myExpert.googleCalendarConnected && !sessionState.impersonateExpertAuthToken) {
      setGoogleLoginStarted(true);
      doGoogleAuth({ includeCalendarScope: true, onError: () => setGoogleLoginStarted(false) });
    }
  }, [doGoogleAuth, myExpert, sessionState.impersonateExpertAuthToken]);

  if (!myExpert) {
    sessionStorage.setItem("postLoginRedirectTo", `${location.pathname}${location.hash}${location.search}`);
    return <Redirect to="/login" />;
  }

  if (googleLoginStarted) {
    return null;
  }

  return (
    <IonSplitPane className={styles.splitPane} contentId="main">
      <ExpertMenu />
      <IonPage id="main">
        <Switch>
          <Route exact path="/expert/journeys" render={() => <ExpertJourneysPage />} />
          <Route exact path="/expert/all-journeys" render={() => <ExpertAllJourneysPage />} />
          <Route exact path="/expert/care-plans" render={() => <ExpertCarePlansPage />} />
          <Route exact path="/expert/consults" render={() => <ExpertConsultsPage />} />
          <Route exact path="/expert/tasks" render={() => <ExpertTasksPage />} />
          <Route exact path="/expert/dashboard" render={() => <ExpertDashboardPage />} />
          <Route exact path="/expert/dashboard/consults/duration" render={() => <ExpertDashboardConsultsPage />} />
          <Route exact path="/expert/dashboard/consults/position" render={() => <ExpertDashboardConsultsPage />} />
          <Route exact path="/expert/dashboard/new-signups" render={() => <ExpertDashboardSignUpsPage />} />
          <Route
            exact
            path="/expert/dashboard/cp-turn-around-time"
            render={() => <ExpertDashboardCPTurnAroundTimePage />}
          />
          <Route exact path="/expert/dashboard/time-logs/billable" render={() => <ExpertDashboardTimeLogsPage />} />
          <Route exact path="/expert/dashboard/time-logs/non-billable" render={() => <ExpertDashboardTimeLogsPage />} />
          <Route exact path="/expert/activities" render={() => <ExpertActivitiesPage />} />
          <Route exact path="/expert/time-logs" render={() => <ExpertTimeLogsPage />} />
          <Route exact path="/expert/account" render={() => <ExpertAccountPage />} />
          <Route exact path="/expert/goal-templates" render={() => <ExpertGoalTemplatesPage />} />
          <Route exact path="/expert/goal-templates/new" render={() => <ExpertGoalTemplateCreatePage />} />
          <Route
            exact
            path="/expert/goal-templates/:id"
            render={params => <ExpertGoalTemplatePage goalTemplateId={params.match.params.id} />}
          />
          <Route
            exact
            path="/expert/goal-templates/:id/edit"
            render={params => <ExpertGoalTemplateEditPage goalTemplateId={params.match.params.id} />}
          />

          <Route
            exact
            path="/expert/automations/:id/applications"
            render={params => <ExpertAutomationApplicationsPage automationId={params.match.params.id} />}
          />
          <Route
            exact
            path="/expert/automations/:automationId/applications/:id"
            render={params => (
              <ExpertAutomationApplicationPage
                automationApplicationId={params.match.params.id}
                automationId={params.match.params.automationId}
              />
            )}
          />
          <Route
            exact
            path="/expert/automations/:id/edit"
            render={params => <ExpertAutomationEditPage automationId={params.match.params.id} />}
          />
          <Route exact path="/expert/automations/new" render={() => <ExpertAutomationNewPage />} />
          <Route
            exact
            path="/expert/automations/:id"
            render={params => <ExpertAutomationPage automationId={params.match.params.id} />}
          />
          <Route exact path="/expert/automations" render={() => <ExpertAutomationsPage />} />

          <Route exact path="/expert/manage-experts" render={() => <ExpertManageExpertsPage />} />
          <Route exact path="/expert/messages" render={() => <ExpertMessagesPage />} />
          <Route
            exact
            path="/expert/messages/:id"
            render={params => <ExpertMessagePage messageId={params.match.params.id} />}
          />
          <Route exact path="/expert/message-templates" render={() => <ExpertMessageTemplatesPage />} />
          <Route
            exact
            path="/expert/message-template-categories"
            render={() => <ExpertMessageTemplateCategoriesPage />}
          />
          <Route
            exact
            path="/expert/manage-experts/:expertId/edit"
            render={params => <ExpertManageExpertEditPage expertId={params.match.params.expertId} />}
          />
          <Route
            exact
            path="/expert/manage-experts/:expertId/journeys"
            render={params => <ExpertManageExpertJourneysPage expertId={params.match.params.expertId} />}
          />
          <Route
            exact
            path="/expert/resource-feature-templates"
            render={() => <ExpertResourceFeatureTemplatesPage />}
          />
          <Route
            exact
            path="/expert/resource-feature-templates/new"
            render={() => <ExpertResourceFeatureTemplateCreatePage />}
          />
          <Route
            exact
            path="/expert/resource-feature-templates/:id/edit"
            render={params => (
              <ExpertResourceFeatureTemplateEditPage resourceFeatureTemplateId={params.match.params.id} />
            )}
          />
          <Route exact path="/expert/tags" render={() => <ExpertJourneyTagsPage />} />
          <Route exact path="/expert/manage-tags" render={() => <ExpertManageJourneyTagsPage />} />
          <Route
            exact
            path="/expert/tags/:tag"
            render={params => <ExpertManageJourneyTaggingsPage tag={params.match.params.tag} />}
          />
          <Route exact path="/expert/ooo-schedule" render={() => <ExpertOutOfOfficeSchedulePage />} />
          <Route
            exact
            path="/expert/package-grant/:id"
            render={params => <ExpertPackageGrantPage packageGrantId={params.match.params.id} />}
          />
          <Route exact path="/expert/employers" render={() => <ExpertEmployersPage />} />
          <Route
            exact
            path="/expert/employers/:employerId"
            render={params => <ExpertEmployerPage employerId={params.match.params.employerId} />}
          />
          <Route
            exact
            path="/expert/ooo-schedule/:outOfOfficePeriodId"
            render={params => (
              <ExpertConsultConflictsPage outOfOfficePeriodId={params.match.params.outOfOfficePeriodId} />
            )}
          />
          <Route exact path="/expert/community" render={() => <ExpertCommunityPage />} />

          <Route exact path="/expert" render={() => <Redirect to="/expert/dashboard" />} />
          <Route render={() => <PageNotFoundWithMenuPage journeyId={null} />} />
        </Switch>
      </IonPage>
    </IonSplitPane>
  );
};

export default ExpertRouter;
